import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import {
  fetchTransferByToken,
  resetTransfer,
  downloadTransferFile,
  clearDownloadError,
} from "../store/transferSlice";
import {
  Clock,
  DownloadCloud,
  CheckCircle2,
  Ban,
  AlertTriangle,
  Calendar,
  Mail,
  Sparkles,
  Lock,
  Eye,
  EyeOff,
  KeyRound,
  AlertCircle,
  FileQuestion,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Progress } from "./ui/progress";
import { getFileMeta, formatBytes } from "../lib/fileTypes";

/* ─── helpers ─── */

const TR_MONTHS = [
  "Oca", "Şub", "Mar", "Nis", "May", "Haz",
  "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara",
];

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const m = TR_MONTHS[d.getMonth()];
  const y = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${m} ${y}, ${hh}:${mm}`;
}

function formatRelativeExpiry(iso) {
  if (!iso) return { label: "—", expired: false };
  const now = new Date();
  const target = new Date(iso);
  const diff = target.getTime() - now.getTime();
  if (diff <= 0) return { label: "Süresi doldu", expired: true };
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  if (days >= 1) return { label: `${days} gün ${hours} saat kaldı`, expired: false };
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return { label: `${hours} saat ${minutes} dakika kaldı`, expired: false };
}

function getInitials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

/* ─── background decoration (shared) ─── */
function Background() {
  return (
    <>
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, #050816 0%, #0B1230 35%, #0F1B4D 70%, #1A1147 100%)",
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -right-40 -top-40 h-[600px] w-[600px] rounded-full opacity-40 blur-3xl"
        style={{
          background:
            "radial-gradient(circle, rgba(251,191,36,0.30) 0%, rgba(251,191,36,0) 70%)",
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -left-40 bottom-0 h-[500px] w-[500px] rounded-full opacity-40 blur-3xl"
        style={{
          background:
            "radial-gradient(circle, rgba(59,130,246,0.30) 0%, rgba(59,130,246,0) 70%)",
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
        aria-hidden
      />
    </>
  );
}

/* ─── header (shared) ─── */
function Header() {
  return (
    <header className="relative z-10 mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-6 sm:px-10">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-amber-300 via-yellow-400 to-amber-600 shadow-[0_0_24px_rgba(251,191,36,0.35)] ring-1 ring-amber-200/40">
          <DownloadCloud className="h-4 w-4 text-zinc-900" strokeWidth={2.5} />
        </div>
        <div className="flex items-baseline gap-2">
          <span className="font-display text-lg font-bold tracking-[0.18em] text-white">
            ALTINGROSS
          </span>
          <span className="hidden h-4 w-px bg-white/20 sm:inline-block" />
          <span className="hidden font-mono text-[11px] uppercase tracking-[0.28em] text-amber-300/80 sm:inline">
            Share
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 backdrop-blur-md">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/70">
          Güvenli transfer
        </span>
      </div>
    </header>
  );
}

/* ─── no token page ─── */
function NoTokenPage() {
  return (
    <div className="relative min-h-screen w-full overflow-hidden text-white">
      <Background />
      <Header />
      <main className="relative z-10 flex min-h-[70vh] flex-col items-center justify-center px-6 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/20 backdrop-blur-md mb-6">
          <FileQuestion className="h-8 w-8 text-amber-300" />
        </div>
        <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">
          Erişim Anahtarı Gerekli
        </h2>
        <p className="mt-3 max-w-sm text-[15px] text-white/60 leading-relaxed">
          Bu sayfaya erişmek için geçerli bir erişim anahtarı (access token) gereklidir. Lütfen size gönderilen bağlantıyı kullanın.
        </p>
      </main>
    </div>
  );
}

/* ─── loading page ─── */
function LoadingPage() {
  return (
    <div className="relative min-h-screen w-full overflow-hidden text-white">
      <Background />
      <Header />
      <main className="relative z-10 flex min-h-[70vh] flex-col items-center justify-center gap-4">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-white/20 border-t-amber-400" />
        <p className="font-mono text-sm uppercase tracking-[0.22em] text-white/50">
          Yükleniyor…
        </p>
      </main>
    </div>
  );
}

/* ─── error page ─── */
function ErrorPage({ error }) {
  return (
    <div className="relative min-h-screen w-full overflow-hidden text-white">
      <Background />
      <Header />
      <main className="relative z-10 flex min-h-[70vh] flex-col items-center justify-center px-6 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-rose-500/20 ring-1 ring-rose-500/40 backdrop-blur-md mb-6">
          <AlertTriangle className="h-8 w-8 text-rose-400" />
        </div>
        <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">
          Transfer Bulunamadı
        </h2>
        <p className="mt-3 max-w-sm text-[15px] text-white/60 leading-relaxed">
          {error || "Geçersiz veya süresi dolmuş bir erişim anahtarı girdiniz."}
        </p>
      </main>
    </div>
  );
}

/* ─── main component ─── */
export default function TransferPage() {
  const { accessToken } = useParams();
  const dispatch = useDispatch();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { data: transfer, loading, error, downloading, downloadError } = useSelector((s) => s.transfer);

  const [unlocked, setUnlocked] = useState(false);
  const [pwdValue, setPwdValue] = useState("");
  const [pwdError, setPwdError] = useState("");
  const [pwdShow, setPwdShow] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (!accessToken || !executeRecaptcha) return;
    const fetch = async () => {
      const reCaptchaToken = await executeRecaptcha("fetch_transfer");
      dispatch(fetchTransferByToken({ accessToken, reCaptchaToken }));
    };
    fetch();
    return () => {
      dispatch(resetTransfer());
    };
  }, [accessToken, executeRecaptcha, dispatch]);

  useEffect(() => {
    setUnlocked(false);
    setPwdValue("");
    setPwdError("");
    dispatch(clearDownloadError());
  }, [accessToken, dispatch]);

  useEffect(() => {
    if (downloadError) toast.error(downloadError);
  }, [downloadError]);


  if (!accessToken) return <NoTokenPage />;
  if (loading) return <LoadingPage />;
  if (error) return <ErrorPage error={error} />;
  if (!transfer) return null;

  /* ── derived values from API response ── */
  const senderName = transfer.senderFullName || "Bilinmiyor";
  const senderInitials = getInitials(senderName);
  const file = transfer.fileEntry || {};
  const extension = (file.extension || "").replace(/^\./, "");

  const expiry = formatRelativeExpiry(transfer.expiryDate);
  const isExpired = !transfer.isActive || expiry.expired;
  const isLimitReached =
    transfer.maxDownloadLimit > 0 &&
    transfer.downloadCount >= transfer.maxDownloadLimit;
  const needsPassword = transfer.isPassword;
  const canDownload = !isExpired && !isLimitReached && (!needsPassword || pwdValue.trim().length > 0);

  const fileMeta = getFileMeta(extension);
  const FileIcon = fileMeta.Icon;
  const downloadsLeft = Math.max(
    0,
    transfer.maxDownloadLimit - transfer.downloadCount
  );
  const limitPct =
    transfer.maxDownloadLimit > 0
      ? Math.min(
          100,
          Math.round(
            (transfer.downloadCount / transfer.maxDownloadLimit) * 100
          )
        )
      : 0;

  const verifyPassword = (e) => {
    e?.preventDefault();
    if (!pwdValue.trim()) {
      setPwdError("Lütfen bir şifre girin.");
      return;
    }
    setVerifying(true);
    setPwdError("");
    setTimeout(() => {
      setUnlocked(true);
      toast.success("Kilit açıldı. Dosya indirilebilir.");
      setVerifying(false);
    }, 450);
  };

  const startDownload = async () => {
    if (!canDownload || downloading) return;

    const reCaptchaToken = executeRecaptcha
      ? await executeRecaptcha("download")
      : null;

    dispatch(
      downloadTransferFile({
        accessToken,
        password: transfer.isPassword ? pwdValue : null,
        fallbackFilename: file.realName || file.name,
        reCaptchaToken,
      })
    ).then(async (action) => {
      if (downloadTransferFile.fulfilled.match(action)) {
        toast.success("İndirme tamamlandı.", {
          description: file.realName || file.name,
        });
        const refetchToken = executeRecaptcha
          ? await executeRecaptcha("fetch_transfer")
          : null;
        dispatch(fetchTransferByToken({ accessToken, reCaptchaToken: refetchToken }));
      }
    });
  };

  return (
    <div
      data-testid="transfer-page"
      className="relative min-h-screen w-full overflow-hidden text-white"
    >
      <Background />

      <Header />

      <main className="relative z-10 mx-auto flex w-full max-w-6xl flex-col items-start gap-10 px-6 pb-24 pt-6 sm:px-10 lg:flex-row lg:items-center lg:gap-16 lg:pt-10">
        {/* Left intro */}
        <section className="w-full max-w-md lg:flex-1">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 backdrop-blur-md">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/60">
              Transfer
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white">
              #{transfer.id.slice(-6).toUpperCase()}
            </span>
          </div>
          <h1 className="font-display mt-5 text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-[56px]">
            Sizin için
            <br />
            <span className="bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-400 bg-clip-text text-transparent">
              güvenli bir transfer
            </span>
            <br />
            hazırlandı.
          </h1>
          <p className="mt-6 max-w-sm text-[15px] leading-relaxed text-white/65">
            <span className="font-medium text-white">{senderName}</span>{" "}
            seninle bir dosya paylaştı. Dosyayı inceleyebilir, geçerli olduğu
            sürece güvenli biçimde indirebilirsin.
          </p>

          <div className="mt-9 hidden gap-3 lg:flex">
            <Stat label="Dosya boyutu" value={formatBytes(file.size)} />
            <Stat
              label="Geçerlilik"
              value={
                expiry.expired
                  ? "Doldu"
                  : `${Math.max(
                      1,
                      Math.ceil(
                        (new Date(transfer.expiryDate).getTime() - Date.now()) /
                          86400000
                      )
                    )} gün`
              }
              danger={expiry.expired}
            />
            <Stat
              label="İndirme"
              value={`${transfer.downloadCount}/${transfer.maxDownloadLimit}`}
            />
          </div>
        </section>

        {/* Right: card */}
        <section className="w-full lg:flex-1">
          <div
            data-testid="transfer-card"
            className="animate-card-in relative mx-auto w-full max-w-xl rounded-2xl border border-white/15 bg-white/[0.97] p-6 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)] backdrop-blur-xl ring-1 ring-black/5 sm:p-8"
          >
            <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" />

            <div className="space-y-6">
              {/* Sender */}
              <div className="flex items-center gap-3">
                <div
                  className="flex h-11 w-11 items-center justify-center rounded-full bg-zinc-900 text-sm font-semibold text-white"
                  data-testid="sender-avatar"
                >
                  {senderInitials}
                </div>
                <div className="min-w-0">
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-400">
                    Gönderen
                  </p>
                  <p className="truncate text-[15px] font-semibold text-zinc-900">
                    {senderName}
                  </p>
                  {transfer.receiverEmail && (
                    <p className="flex items-center gap-1 truncate text-xs text-zinc-500">
                      <Mail className="h-3 w-3" />
                      {transfer.receiverEmail}
                    </p>
                  )}
                </div>
              </div>

              {/* Status banner (expired / limit) */}
              {isExpired && (
                <div
                  data-testid="status-banner"
                  className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-800"
                >
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                  <div className="text-sm">
                    <p className="font-semibold">Bu transferin süresi doldu.</p>
                    <p className="mt-0.5 text-rose-700/80">
                      Geçerlilik {formatDate(transfer.expiryDate)} tarihinde
                      sona erdi.
                    </p>
                  </div>
                </div>
              )}
              {!isExpired && isLimitReached && (
                <div
                  data-testid="status-banner"
                  className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900"
                >
                  <Ban className="mt-0.5 h-5 w-5 shrink-0" />
                  <div className="text-sm">
                    <p className="font-semibold">İndirme limitine ulaşıldı.</p>
                    <p className="mt-0.5 text-amber-800/80">
                      İzin verilen{" "}
                      <span className="font-mono">
                        {transfer.maxDownloadLimit}
                      </span>{" "}
                      indirme hakkının tamamı kullanıldı.
                    </p>
                  </div>
                </div>
              )}

              {/* File card */}
              <div
                data-testid="file-card"
                className="flex items-center gap-4 rounded-xl border border-zinc-200 bg-white px-4 py-4 transition-colors hover:bg-zinc-50"
              >
                <div
                  className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-xl ${fileMeta.tint}`}
                >
                  <FileIcon
                    className={`h-7 w-7 ${fileMeta.accent}`}
                    strokeWidth={1.8}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <p
                    className="truncate text-[15px] font-semibold text-zinc-900"
                    title={file.realName || file.name}
                    data-testid="file-name"
                  >
                    {file.realName || file.name}
                  </p>
                  <div className="mt-1 flex items-center gap-2 text-xs text-zinc-500">
                    <span
                      className="rounded-md bg-zinc-100 px-1.5 py-0.5 font-mono uppercase tracking-wider text-zinc-700"
                      data-testid="file-extension"
                    >
                      {fileMeta.label}
                    </span>
                    <span className="h-1 w-1 rounded-full bg-zinc-300" />
                    <span className="font-mono" data-testid="file-size">
                      {formatBytes(file.size)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Meta row */}
              <div className="grid grid-cols-2 gap-3">
                <MetaRow
                  Icon={Calendar}
                  label="Son Tarih"
                  value={formatDate(transfer.expiryDate)}
                  danger={isExpired}
                />
                <MetaRow
                  Icon={Clock}
                  label="Geçerlilik"
                  value={expiry.label}
                  danger={expiry.expired}
                />
              </div>

              {/* Password input */}
              {needsPassword && !isExpired && !isLimitReached && (
                <form
                  onSubmit={verifyPassword}
                  className="space-y-2"
                  data-testid="password-form"
                >
                  <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                    <Lock className="h-3 w-3" />
                    Bu dosya şifre korumalı
                  </label>
                  <div className="relative">
                    <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                    <Input
                      type={pwdShow ? "text" : "password"}
                      value={pwdValue}
                      onChange={(e) => {
                        setPwdValue(e.target.value);
                        if (pwdError) setPwdError("");
                      }}
                      placeholder="Transfer şifresini girin"
                      data-testid="password-input"
                      autoFocus
                      className="h-12 rounded-xl border-zinc-300 bg-white pl-10 pr-12 text-base text-black focus-visible:ring-[hsl(var(--primary))] focus-visible:border-[hsl(var(--primary))]"
                    />
                    <button
                      type="button"
                      onClick={() => setPwdShow((s) => !s)}
                      data-testid="password-toggle"
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-2 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
                      aria-label={pwdShow ? "Şifreyi gizle" : "Şifreyi göster"}
                    >
                      {pwdShow ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {pwdError && (
                    <div
                      className="flex items-center gap-2 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700"
                      data-testid="password-error"
                    >
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      <span>{pwdError}</span>
                    </div>
                  )}
                </form>
              )}

              {/* Download stats */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-mono uppercase tracking-[0.18em] text-zinc-400">
                    İndirme
                  </span>
                  <span
                    data-testid="download-counter"
                    className="font-mono text-zinc-700"
                  >
                    {transfer.downloadCount}{" "}
                    <span className="text-zinc-400">/</span>{" "}
                    {transfer.maxDownloadLimit}
                  </span>
                </div>
                <Progress
                  value={limitPct}
                  data-testid="download-progress"
                  className="h-1.5 bg-zinc-100"
                />
                <p className="text-[11px] text-zinc-500">
                  {isLimitReached
                    ? "İndirme hakkı kalmadı."
                    : `${downloadsLeft} indirme hakkı kaldı.`}
                </p>
              </div>

              {/* Action button */}
              <div className="space-y-3">
                <Button
                  type="button"
                  data-testid="download-btn"
                  disabled={!canDownload || downloading}
                  onClick={startDownload}
                  className="group h-14 w-full rounded-xl bg-[hsl(var(--primary))] text-base font-semibold text-white shadow-md transition-all hover:bg-[hsl(var(--primary))]/90 hover:shadow-lg active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {downloading ? (
                    <>
                      <Sparkles className="h-5 w-5 animate-pulse" />
                      İndiriliyor…
                    </>
                  ) : isExpired ? (
                    <>
                      <Clock className="h-5 w-5" />
                      Süresi doldu
                    </>
                  ) : isLimitReached ? (
                    <>
                      <Ban className="h-5 w-5" />
                      Limit doldu
                    </>
                  ) : (
                    <>
                      <DownloadCloud className="h-5 w-5 transition-transform group-hover:-translate-y-0.5" />
                      Dosyayı indir · {formatBytes(file.size)}
                    </>
                  )}
                </Button>

                {downloading && (
                  <div className="flex items-center justify-center gap-2 text-xs text-zinc-500">
                    <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-zinc-300 border-t-zinc-600" />
                    Dosya indiriliyor, lütfen bekleyin…
                  </div>
                )}

                {canDownload && !downloading && (
                  <p className="flex items-center justify-center gap-1.5 text-center text-xs text-zinc-500">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                    Bağlantı güvenli · Transfer ID:{" "}
                    <span className="font-mono text-zinc-700">
                      {transfer.id}
                    </span>
                  </p>
                )}
              </div>
            </div>
          </div>

          <p className="mx-auto mt-5 max-w-xl text-center font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">
            &copy; 2026 ALTINGROSS &mdash; Tüm hakları saklıdır.
          </p>
        </section>
      </main>
    </div>
  );
}

/* ─── sub-components ─── */

function MetaRow({ Icon, label, value, danger = false }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5">
      <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-400">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <p
        className={`mt-1 truncate text-sm font-medium ${
          danger ? "text-rose-600" : "text-zinc-900"
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function Stat({ label, value, danger = false }) {
  return (
    <div className="flex-1 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 backdrop-blur-md transition-colors hover:bg-white/[0.06]">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
        {label}
      </p>
      <p
        className={`mt-1 font-display text-lg font-semibold tracking-tight ${
          danger ? "text-rose-300" : "text-white"
        }`}
      >
        {value}
      </p>
    </div>
  );
}
